/**
 * Site templates (M3-02) — the hand-shaped places, as authored data.
 *
 * A template is string-art plus a legend. That form is chosen over a
 * coordinate list for one reason and it is worth the ugliness of counting
 * columns: **you can see the building**. A hall written as
 * `[{x:3,y:2,kind:'wall'}, …]` is forty lines nobody can review; a hall drawn
 * as `#___+` is a hall, and a reviewer spots the door on the wrong wall in a
 * second. Everything the game has that a person authored should be legible to
 * the person who has to change it.
 *
 * Placement is *not* here — worldgen decides where these go, against the
 * terrain it generated. This file only knows what a village looks like, never
 * where the village is. That split is what lets the same village land in a
 * different meadow on every seed.
 *
 * `'.'` means **leave the terrain alone**. A template is a stencil, not a
 * rectangle of ground: the gaps between the buildings should still be the
 * meadow the village was built on, and the corners of the stencil should not
 * stamp a square of dirt into the wood behind it.
 */
/** The glyph that means "no change". */
export const KEEP = '.';
/* -------------------------------------------------------------------------- *
 * Llan-y-Nant — the village
 * -------------------------------------------------------------------------- */
/**
 * Hall with a hearth, the healer's house, the smithy, the herders' home, the
 * well, the physic garden and the fold. Its sacred ground is not part of this
 * footprint: the prehistoric circle stands separately in open country to the
 * south, old and holy enough that the village grew near it without building on
 * it.
 *
 * Laid out round an open middle rather than along a street, because that is
 * what a llan is — an enclosure with the important building in it — and
 * because an open middle is where eight players will actually meet.
 *
 * M5-01 split the one fenced square into two places that had been sharing it.
 * The garden and the pen were the same eight tiles, which meant the Meddyg
 * spent her mornings weeding among the cattle; the fold is now the fenced
 * ground by the herders' door and the garden is a dug plot up by the well,
 * where the water is.
 */
const VILLAGE = {
    id: 'village',
    name: 'Llan-y-Nant',
    rows: [
        '.......................',
        '...:::::::::::::::::...',
        '..::#########:ggg::::..',
        '..::#_______#:ggg::::..',
        '..::#___=___+:ggg::::..',
        '..::#_______#:::::o:::.',
        '..::####+####::::::::..',
        '..:::::::::::::::::::..',
        '..:#####:::::::#####:..',
        '..:#___#:::::::#_=_#:..',
        '..:#___+:::::::+__c#:..',
        '..:#####:::::::#####:..',
        '..:::::::::::::::::::..',
        '..::ffffffff::#####::..',
        '..::f......f::#_=_#::..',
        '..::f......+::#___+::..',
        '..::ffffffff::#####::..',
        '.......................',
    ],
    legend: {
        ':': 'dirt',
        '#': 'wall',
        _: 'floor',
        '+': 'door',
        '=': 'hearth',
        o: 'well',
        f: 'fence',
        // M4-08. The Meddyg's cot, in the right-hand house beside its hearth. One
        // tile, and the only one in the valley.
        c: 'cot',
        // M5-01. Her physic garden, which used to be the pen and is now its own
        // ground: dug rigs up by the well, where the watering is.
        g: 'garden',
    },
    /**
     * Anchors are *standable ground*, not the furniture they are named after.
     *
     * A hearth, a well and a forge are all impassable, and a schedule stop on one
     * is a stop nobody can ever reach — the villager walks at it for ever and
     * stands beside it looking like a bug. So `healer` is the floor beside her
     * fire rather than the fire, and `wellside` is the dirt beside the well while
     * `well` and `water` stay on the well itself, because M4-02 cares which water
     * you drank and that is a fact about the tile.
     */
    anchors: {
        // The middle of the green, between the hall door and the two houses. New
        // arrivals begin among the living; rebirth belongs to the separate circle.
        anchor: [11, 7],
        hall: [7, 4],
        /** Where M5-05 hangs what you killed. Over the hall's hearth. */
        trophy: [8, 4],
        well: [18, 5],
        /** M4-02 cares which water you drank: this is the clean one. */
        water: [18, 5],
        /** Where somebody stands to draw it. */
        wellside: [17, 5],
        /**
         * M4-08's Meddyg. `healer` is the floor beside her fire — the cot is for
         * whoever needs it — and `garden` is the physic bed up by the well.
         */
        healer: [16, 9],
        cot: [18, 10],
        garden: [15, 3],
        /**
         * M5-01's four. The `forge` is the smith's fire and `smithy` is where he
         * stands to work it; the `fold` is the far end of the pen, so two herders
         * minding the same animals are not both trying to stand on one tile.
         */
        smithy: [16, 15],
        forge: [16, 14],
        pen: [7, 14],
        fold: [9, 15],
        home: [4, 9],
        fireside: [6, 9],
    },
};
/**
 * A prehistoric sanctuary in its own open clearing south of Llan-y-Nant.
 *
 * The settlement does not trespass inside it. Eight stones hold a proper ring
 * around the carved Sun Stone, with grass between them and sky overhead; the
 * path may approach the threshold, but no house, fold or garden belongs here.
 */
const REBIRTH_CIRCLE = {
    id: 'rebirth-circle',
    name: 'the Sun Stone circle',
    rows: [
        '......,,,,,,,......',
        '....,,,,,m,,,,,....',
        '..,,,m,,,,,,,m,,,..',
        '.,,,,,,,,,,,,,,,,,.',
        '.,,,,,,,,,,,,,,,,,.',
        '.,,,,,,,,,,,,,,,,,.',
        '.,m,,,,,,S,,,,,,m,.',
        '.,,,,,,,,,,,,,,,,,.',
        '.,,,,,,,,,,,,,,,,,.',
        '.,,,,,,,,,,,,,,,,,.',
        '..,,,m,,,,,,,m,,,..',
        '....,,,,,m,,,,,....',
        '......,,,,,,,......',
    ],
    legend: { ',': 'grass', m: 'menhir', S: 'sun-stone' },
    anchors: { anchor: [9, 6], rebirth: [9, 6] },
};
/* -------------------------------------------------------------------------- *
 * The thin place
 * -------------------------------------------------------------------------- */
/**
 * Eight stones and the Pair Dadeni, on the open moor.
 *
 * Spaced, not walled. A closed ring reads as a building and would also be one
 * mechanically — impassable stone all the way round — and the horror of a thin
 * place is that you can walk into it. The gaps are the point.
 */
const THIN_PLACE = {
    id: 'thin-place',
    name: 'the thin place',
    rows: [
        '.............',
        '......m......',
        '..m.......m..',
        '.............',
        '.............',
        '.............',
        '.m....C....m.',
        '.............',
        '.............',
        '.............',
        '..m.......m..',
        '......m......',
        '.............',
    ],
    legend: { m: 'menhir', C: 'cauldron' },
    anchors: {
        anchor: [6, 6],
        /** M4-07's stage. The tile itself is impassable — you stand beside it. */
        cauldron: [6, 6],
    },
};
/* -------------------------------------------------------------------------- *
 * The smaller places
 * -------------------------------------------------------------------------- */
const HERMIT_CELL = {
    id: 'hermit-cell',
    name: "the hermit's cell",
    rows: ['.......', '.#####.', '.#___#.', '.#_=_#.', '.##+##.'],
    legend: { '#': 'wall', _: 'floor', '=': 'hearth', '+': 'door' },
    anchors: { anchor: [3, 4], hearth: [3, 3] },
};
/**
 * A bothy (RC1-07) — a rough stick hut with a roof and a dry floor, and no
 * hearth.
 *
 * The point of it is *cover*, not a free fire: a couple of these stand out in
 * the valley so a player caught in the rain — when a fire will barely light and
 * the open ground rests you `exposed` — has somewhere to get dry and lay their
 * own fire under a roof, which lights readily. Deliberately hearthless, or it
 * would be a second village rather than a shelter; the floor is what earns the
 * `dry` rest and the sheltered kindling, through `isSheltered`.
 */
const BOTHY = {
    id: 'bothy',
    name: 'a bothy',
    rows: ['.....', '.###.', '.#_#.', '.#_#.', '.#+#.'],
    legend: { '#': 'wall', _: 'floor', '+': 'door' },
    anchors: { anchor: [2, 3] },
};
const BARROW = {
    id: 'barrow',
    name: 'a burial mound',
    rows: ['..MMM..', '.MMMMM.', 'MMMmMMM', '.MMMMM.', '..MMM..'],
    legend: { M: 'mound', m: 'menhir' },
    anchors: { anchor: [3, 4] },
};
/**
 * A wolf den: a scrape under a bank of broken rock.
 *
 * Three tiles wide because a den has to be findable without being a dungeon.
 * The crag above it is what makes it read as a *place animals come from* on a
 * glyph grid — a bare dirt patch in a wood is nothing.
 */
const WOLF_DEN = {
    id: 'wolf-den',
    name: 'a wolf den',
    rows: ['.###.', '#:::#', '..:..'],
    legend: { '#': 'crag', ':': 'dirt' },
    anchors: { anchor: [2, 2], spawn: [2, 1] },
};
/**
 * A stag glade (M3-10) — a clearing in the wood where the deer lie up.
 *
 * The cheapest possible version of "stags exist". `STAG_INSTINCT` and
 * `STAG_BEAST` have been real content since M2-05 and worldgen never placed
 * them, which made M3-08's headline criterion — trail a wounded stag to where it
 * fell — name an animal that could not be found in the valley.
 *
 * Almost all `KEEP`, and that is the whole template: what makes a glade is the
 * *absence* of trees, and the wood around it is already there. The grass at the
 * middle is the couch — the trodden patch a hunter reads as "something has been
 * lying here", once M3-08's droppings accumulate on it.
 */
const STAG_GLADE = {
    id: 'stag-glade',
    name: 'a glade',
    rows: ['..ggg..', '.ggggg.', 'ggggggg', '.ggggg.', '..ggg..'],
    legend: { g: 'grass' },
    anchors: { anchor: [3, 2], spawn: [3, 2] },
};
/** The boar's wallow. Dormant until M5-04; the ground is here from M3-02. */
const WALLOW = {
    id: 'wallow',
    name: 'a wallow',
    rows: ['..W..', '.WWW.', 'WWWWW', '.WWW.', '..W..'],
    legend: { W: 'wallow' },
    anchors: { anchor: [2, 2], spawn: [2, 2] },
};
export const SITE_TEMPLATES = [
    VILLAGE,
    REBIRTH_CIRCLE,
    THIN_PLACE,
    HERMIT_CELL,
    BOTHY,
    BARROW,
    WOLF_DEN,
    WALLOW,
    STAG_GLADE,
];
export function siteTemplate(id) {
    const found = SITE_TEMPLATES.find((template) => template.id === id);
    if (!found)
        throw new Error(`no site template "${id}"`);
    return found;
}
export function templateWidth(template) {
    return (template.rows[0] ?? '').length;
}
export function templateHeight(template) {
    return template.rows.length;
}
/**
 * Validate every template at module load.
 *
 * String-art's one weakness is that a row one character short is invisible to
 * the eye and catastrophic to the stamp — it shifts every tile after it left by
 * one, which puts the door inside the wall. Checking at load turns that into a
 * failure at `import` time, next to the art, instead of a subtly wrong village
 * on one seed in twelve.
 */
function checkTemplates() {
    for (const template of SITE_TEMPLATES) {
        const width = templateWidth(template);
        if (width === 0 || template.rows.length === 0) {
            throw new Error(`site template "${template.id}" is empty`);
        }
        for (const [index, row] of template.rows.entries()) {
            if (row.length !== width) {
                throw new Error(`site template "${template.id}" row ${index} is ${row.length} wide, expected ${width}`);
            }
            for (const glyph of row) {
                if (glyph !== KEEP && template.legend[glyph] === undefined) {
                    throw new Error(`site template "${template.id}" uses "${glyph}", which has no legend`);
                }
            }
        }
        const anchor = template.anchors['anchor'];
        if (!anchor)
            throw new Error(`site template "${template.id}" has no "anchor"`);
        for (const [name, [x, y]] of Object.entries(template.anchors)) {
            if (x < 0 || y < 0 || x >= width || y >= template.rows.length) {
                throw new Error(`site template "${template.id}" anchor "${name}" is outside the template`);
            }
        }
    }
}
checkTemplates();
//# sourceMappingURL=sites.js.map